import { AlertPadding, AlertPosition } from "@components/alert/Alert.types";
import { darken, desaturate, ellipsis, lighten, saturate } from "polished";
import styled, { css } from "styled-components";

import { Status } from "../../enums";
import {
    fadeOutAnimation,
    getSemanticBgColor,
    getSemanticElColor,
    LocalStackOrder,
    T_ALERT_SUBTITLE,
    T_HEADER_small,
    T_PLAIN_small,
    T_PLAIN_small_hig,
    T_TITLE_3
} from "../../global.style";

interface IAlertStatus {
    $status: Status;
    $useFade?: boolean;
    $position?: AlertPosition;
}

const ACTION_ICON_RIGHT_MARGIN = 17;
const ACTION_ICON_RIGHT_MARGIN_SMALL = 2;
const ACTION_ICON_SIZE = 26;
const ACTION_ICON_TOP = 12;

export const ALERT_BOTTOM_MARGIN = 19;
export const ALERT_TOP_MARGIN = -17; // negate bottom margin of previous element

interface IAlert {
    // width max-content, but smaller than 500px
    $small?: boolean;
    // width 100%
    $isFullWidth?: boolean;
    // type of alert padding
    $padding?: AlertPadding;
    // whether content should be shown in one line
    $inline?: boolean;
    $shouldAddBottomMargin?: boolean;

    $hasInlineDetailAndCloseIcon?: boolean;
}

export const StyledAlert = styled.div<IAlertStatus & IAlert & { $isFading: boolean }>`
    position: relative;
    width: ${props => props.$isFullWidth ? "100%" : "max-content"};
    max-width: ${props => props.$small ? "400px" : (!props.$isFullWidth ? "700px" : "none")};
    color: ${props => props.theme.C_TEXT_primary};
    margin-bottom: ${props => props.$shouldAddBottomMargin ? "19px" : null};
    z-index: ${LocalStackOrder.Tooltips};

    &::before {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: ${props => props.$small || props.$inline ? 0 : "22px"};
        border-radius: 3px;
        background-color: ${props => getSemanticBgColor(props.theme, props.$status, !props.$small)};
        z-index: -1;

        ${props => props.$position !== AlertPosition.Default && css`
            // absolutely positioned alerts have shadow
            box-shadow: rgba(0, 0, 0, 0.15) 0 0 5px 0;
        `}
    }

    ${props => props.$useFade && (props.$isFading ? css`
        ${fadeOutAnimation(0)}
    ` : css`
        opacity: 1;
        animation: none;
    `)};

    ${props => props.$position !== AlertPosition.Default && css`
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: ${props.$position === AlertPosition.CenteredBottom ? "150px" : "auto"};
    `}
    & + & {
        margin-top: 5px;
    }
`;

export const StatusIconWrapper = styled.div`
    position: absolute;
    width: 42px;
    height: 42px;
    top: 50%;
    left: 0;
    transform: translate(0, -50%);
`;

export const ActionWrapper = styled.div<{ $status: Status; $isSmall: boolean; $isInline: boolean; }>`
    display: flex;
    position: absolute;
    top: ${props => !(props.$isSmall || props.$isInline) ? `${ACTION_ICON_TOP}px` : "50%"};
    right: ${props => props.$isSmall ? `${ACTION_ICON_RIGHT_MARGIN_SMALL}px` : `${ACTION_ICON_RIGHT_MARGIN}px`};
    transform: ${props => !(props.$isSmall || props.$isInline) ? null : "translateY(-50%)"};

    button {
        &:hover {
            color: ${props => saturate(0.1, lighten(0.1, getSemanticElColor(props.theme, props.$status)))};
        }

        &:active {
            color: ${props => desaturate(0.1, darken(0.1, getSemanticElColor(props.theme, props.$status)))};
        }
    }
`;

const OneLinerPadding = 14;

const getBodyPadding = (padding: AlertPadding): string => {
    switch (padding) {
        case AlertPadding.L:
            return "17px 60px 17px 81px";
        case AlertPadding.M:
            return `${OneLinerPadding}px 19px ${OneLinerPadding}px 81px`;
        case AlertPadding.S:
            return "7px 12px";
    }

};

export const Body = styled.div<IAlert>`
    padding: ${props => getBodyPadding(props.$padding)};
    padding-right: ${props => props.$hasInlineDetailAndCloseIcon ? (props.$small ? `${2 * ACTION_ICON_RIGHT_MARGIN_SMALL + ACTION_ICON_SIZE}px` : `${2 * ACTION_ICON_RIGHT_MARGIN + ACTION_ICON_SIZE}px`) : null};
    white-space: ${props => props.$inline ? "default" : "pre-wrap"};
    word-break: break-word;
    font-weight: normal;

    ${props => props.$inline && ellipsis()};
    display: ${props => props.$inline ? "block" : "flex"};
    justify-content: flex-start;
    align-items: flex-start;

    ${props => !props.$inline && css`
        flex-direction: column;
    `};

    ${props => (props.$inline || props.$small) && css`
        ${T_PLAIN_small};
    `};
`;

export const Title = styled.h4<IAlert & { $disableDefaultStyles: boolean }>`
    ${props => !props.$small && css<{ $disableDefaultStyles: boolean }>`
        ${T_HEADER_small};
        font-weight: ${props => props.$disableDefaultStyles ? "default" : "bold"};
    `};

    ${props => props.$small && css`
        ${T_ALERT_SUBTITLE};
    `};

    ${props => props.$inline && css`
        margin-right: 1ex;
        ${T_PLAIN_small_hig};
        display: inline;
    `};
    
    ${props => !props.$small && !props.$inline && css`
        margin-bottom: 2px;
    `};
`;

export const Subtitle = styled.div<IAlert & IAlertStatus & { $showDivider: boolean; $shortened: boolean }>`
    width: 100%;
    margin: 0;
    ${T_TITLE_3};

    ${props => !props.$inline && props.$showDivider && css<IAlertStatus>`
        &::before {
            content: " ";
            display: block;
            height: 1px;
            margin: 12px 0;
            background-color: ${props => getSemanticElColor(props.theme, props.$status)};
        }
    `};

    ${props => props.$inline && css`
        display: inline;
    `};
    
    ${props => props.$shortened && css`
        &::after { content: "..."; }
    `};
    
    ${props => props.$small && css`
        ${T_PLAIN_small};
    `};
`;

export const ObjectWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 3px;
    max-width: 544px;
    min-width: 300px;
    max-height: 235px;
`;

export const ObjectContent = styled.div<{ $isString: boolean; }>`
    ${props => props.$isString && css`
        word-break: break-word;
    `};
`;

export const AlertDetailWrapper = styled.div <{
    $isInline: boolean;
    $isSmall?: boolean;
}>`
    ${props => !props.$isInline && `
        position: absolute;
        bottom: ${ACTION_ICON_TOP}px;
        right: ${props.$isSmall ? `${ACTION_ICON_RIGHT_MARGIN_SMALL}px` : `${ACTION_ICON_RIGHT_MARGIN}px`};
    `};
`;